import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import colors from '../../../../styles/colors';
import { apiCreateCard, apiUpdateCard } from '../../../../api/cardApi';
import { apiGetAppSettings } from '../../../../api/appSettingsApi';
import type { Card, CardRarity } from '../../../../types/models/card';
import type { Wrestler, PPVGeneral, Belt } from '../../../../types';
import type { UserListItem } from '../../../../types/models/user';

interface RefData {
  wrestlers: Wrestler[];
  ppvs: PPVGeneral[];
  belts: Belt[];
  users: UserListItem[];
}

interface CardCreateModalProps {
  refData: RefData | null;
  refDataLoading: boolean;
  onClose: () => void;
  onCreated: (card: Card) => void;
  card?: Card;
  onUpdated?: (card: Card) => void;
}

// ─── SearchableMultiSelect ────────────────────────────────────────────────────

interface SearchableMultiSelectProps {
  items: { id: number; label: string }[];
  selected: number[];
  onChange: (ids: number[]) => void;
  placeholder: string;
  disabled?: boolean;
}

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  items,
  selected,
  onChange,
  placeholder,
  disabled,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = query.trim()
    ? items.filter((i) => i.label.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : items.slice(0, 8);

  const selectedItems = items.filter((i) => selected.includes(i.id));

  const handleSelect = (id: number) => {
    if (!selected.includes(id)) onChange([...selected, id]);
    setQuery('');
    setOpen(false);
  };

  const handleRemove = (id: number) => {
    onChange(selected.filter((s) => s !== id));
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 150);
  };

  return (
    <MultiSelectWrapper>
      <MultiSelectInput
        type="text"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
      />
      {open && filtered.length > 0 && (
        <Dropdown>
          {filtered.map((item) => (
            <DropdownItem
              key={item.id}
              onMouseDown={() => handleSelect(item.id)}
              isSelected={selected.includes(item.id)}
            >
              {item.label}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
      {selectedItems.length > 0 && (
        <ChipsRow>
          {selectedItems.map((item) => (
            <Chip key={item.id}>
              {item.label}
              <ChipRemove
                type="button"
                disabled={disabled}
                onClick={() => handleRemove(item.id)}
              >
                ✕
              </ChipRemove>
            </Chip>
          ))}
        </ChipsRow>
      )}
    </MultiSelectWrapper>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 620px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: gold;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const SpinnerText = styled.p`
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  padding: 32px 0;
  margin: 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
`;

const PriceHint = styled.button`
  background: none;
  border: 1px solid rgba(255,215,0,0.3);
  border-radius: 4px;
  color: rgba(255,215,0,0.7);
  font-size: 11px;
  padding: 3px 8px;
  cursor: pointer;
  margin-top: 6px;
  &:hover { background-color: rgba(255,215,0,0.08); color: gold; }
`;

const ToggleBtn = styled.button<{ active: boolean }>`
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => (active ? 'gold' : 'rgba(255,255,255,0.2)')};
  background-color: ${({ active }) => (active ? 'rgba(255,215,0,0.15)' : 'transparent')};
  color: ${({ active }) => (active ? 'gold' : 'rgba(255,255,255,0.5)')};
  font-size: 12px;
  cursor: pointer;
`;

const Input = styled.input`
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  &:focus { outline: none; border-color: gold; }
  &:disabled { opacity: 0.5; }
`;

const Textarea = styled.textarea`
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 72px;
  &:focus { outline: none; border-color: gold; }
  &:disabled { opacity: 0.5; }
`;

const Select = styled.select`
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  &:focus { outline: none; border-color: gold; }
  &:disabled { opacity: 0.5; }
  option { background-color: #1a1a1a; }
`;

const SpecificSelect = styled(Select)`
  margin-top: 6px;
  width: 100%;
`;

const ErrorMsg = styled.p`
  margin: 0;
  color: #f87171;
  font-size: 12px;
`;

const GeneralError = styled.p`
  margin: 0;
  padding: 8px 12px;
  background-color: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.3);
  border-radius: 4px;
  color: #f87171;
  font-size: 13px;
`;

const BtnSecondary = styled.button`
  padding: 8px 18px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  cursor: pointer;
  &:hover { background-color: rgba(255,255,255,0.05); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BtnPrimary = styled.button`
  padding: 8px 22px;
  border-radius: 4px;
  border: none;
  background-color: gold;
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background-color: #e6c200; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// SearchableMultiSelect styled
const MultiSelectWrapper = styled.div`
  position: relative;
`;

const MultiSelectInput = styled(Input)``;

const Dropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  margin: 2px 0 0;
  padding: 4px 0;
  list-style: none;
  z-index: 10;
  max-height: 220px;
  overflow-y: auto;
`;

const DropdownItem = styled.li<{ isSelected: boolean }>`
  padding: 8px 12px;
  font-size: 13px;
  color: ${({ isSelected }) => (isSelected ? 'rgba(255,255,255,0.4)' : '#fff')};
  cursor: ${({ isSelected }) => (isSelected ? 'default' : 'pointer')};
  &:hover { background-color: rgba(255, 215, 0, 0.1); }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  background-color: rgba(255, 215, 0, 0.15);
  border: 1px solid rgba(255, 215, 0, 0.4);
  color: gold;
  font-size: 12px;
`;

const ChipRemove = styled.button`
  background: none;
  border: none;
  color: gold;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.7;
  &:hover { opacity: 1; }
  &:disabled { cursor: not-allowed; }
`;

const CurrentAuthorBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: rgba(255, 215, 0, 0.06);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 2px;

  span {
    color: rgba(255, 215, 0, 0.85);
    font-weight: 600;
  }
`;

// ─── Componente principale ────────────────────────────────────────────────────

const RARITIES: CardRarity[] = ['common', 'rare', 'epic', 'legendary'];

// Normalizza qualsiasi formato data a YYYY-MM-DD per input type="date"
const toInputDate = (val: string | null | undefined): string => {
  if (!val) return '';
  const parts = val.split('-');
  if (parts.length !== 3) return '';
  // già YYYY-MM-DD (primo segmento è l'anno)
  if (parts[0].length === 4) return val;
  // DD-MM-YYYY (legacy)
  const [d, m, y] = parts;
  if (!d || !m || !y) return '';
  return `${y}-${m}-${d}`;
};

const CardCreateModal: React.FC<CardCreateModalProps> = ({
  refData,
  refDataLoading,
  onClose,
  onCreated,
  card,
  onUpdated,
}) => {
  const isEdit = !!card;

  const initAuthorMode = (): 'user' | 'text' => {
    if (card?.author_user_id) return 'user';
    if (card?.author_name) return 'text';
    return 'user';
  };
  const [authorMode, setAuthorMode] = useState<'user' | 'text'>(initAuthorMode);
  const [authorUserId, setAuthorUserId] = useState<number | null>(card?.author_user_id ?? null);
  const [authorUserQuery, setAuthorUserQuery] = useState(
    card?.author_user?.username ?? ''
  );
  const [authorUserOpen, setAuthorUserOpen] = useState(false);
  const [authorText, setAuthorText] = useState(card?.author_name ?? '');

  const [fronteMode, setFronteMode] = useState<'file' | 'path'>(card?.fronte_path ? 'path' : 'file');
  const [fronteFile, setFronteFile] = useState<File | null>(null);
  const [frontePath, setFrontePath] = useState(card?.fronte_path ?? '');
  const [retroMode, setRetroMode] = useState<'file' | 'path'>(card?.retro_path ? 'path' : 'file');
  const [retroFile, setRetroFile] = useState<File | null>(null);
  const [retroPath, setRetroPath] = useState(card?.retro_path ?? '');
  const [rarity, setRarity] = useState<CardRarity>(card?.rarity ?? 'common');
  const [notes, setNotes] = useState(card?.notes ?? '');
  const [selectedWrestlerIds, setSelectedWrestlerIds] = useState<number[]>(card?.wrestlers.map((w) => w.id) ?? []);
  const [selectedPpvSpecifics, setSelectedPpvSpecifics] = useState<{ id: number; label: string }[]>(
    card?.ppvs.map((p) => ({
      id: p.id,
      label: p.ppv_description ? `${p.ppv_description.name} ${(p.date ?? '').split('-')[2] ?? ''}`.trim() : `PPV #${p.id}`,
    })) ?? []
  );
  const [generalQuery, setGeneralQuery] = useState('');
  const [generalOpen, setGeneralOpen] = useState(false);
  const [selectedGeneralId, setSelectedGeneralId] = useState<number | null>(null);
  const [selectedBeltIds, setSelectedBeltIds] = useState<number[]>(card?.belts.map((b) => b.id) ?? []);
  const [tagNames, setTagNames] = useState(card?.tags.map((t) => t.name).join(', ') ?? '');
  const [price, setPrice] = useState<string>(card?.price != null ? String(card.price) : '');
  const [shopDisabled, setShopDisabled] = useState<boolean>(card?.shop_disabled ?? false);
  const [referenceUrl, setReferenceUrl] = useState(card?.reference_url ?? '');
  const [referenceString, setReferenceString] = useState(card?.reference_string ?? '');
  const [referenceData, setReferenceData] = useState(toInputDate(card?.reference_data));
  const [defaultPrices, setDefaultPrices] = useState<Partial<Record<CardRarity, number>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fronteInputRef = useRef<HTMLInputElement>(null);
  const retroInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiGetAppSettings().then(res => {
      const map: Partial<Record<CardRarity, number>> = {};
      res.data.forEach(s => {
        const match = s.key.match(/^card_default_price_(common|rare|epic|legendary)$/);
        if (match) map[match[1] as CardRarity] = parseInt(s.value, 10);
      });
      setDefaultPrices(map);
    }).catch(() => {});
  }, []);

  const filteredUsers = (refData?.users ?? [])
    .filter((u) => !authorUserQuery.trim() || u.username.toLowerCase().includes(authorUserQuery.toLowerCase()) || (u.nickname_forum ?? '').toLowerCase().includes(authorUserQuery.toLowerCase()))
    .slice(0, 8);
  const selectedUser = (refData?.users ?? []).find((u) => u.id === authorUserId) ?? null;

  const wrestlerItems = (refData?.wrestlers ?? []).map((w) => ({ id: w.id, label: w.name }));
  const beltItems = (refData?.belts ?? []).map((b) => ({ id: b.id, label: b.name }));
  const filteredGenerals = (refData?.ppvs ?? [])
    .filter((g) => !generalQuery.trim() || g.name.toLowerCase().includes(generalQuery.toLowerCase()))
    .slice(0, 8);
  const selectedGeneral = (refData?.ppvs ?? []).find((g) => g.id === selectedGeneralId) ?? null;

  const handleSubmit = async () => {
    setIsSaving(true);
    setErrors({});
    const fd = new FormData();
    if (fronteMode === 'file' && fronteFile) fd.append('fronte_file', fronteFile);
    else if (frontePath) fd.append('fronte_path', frontePath);
    if (retroMode === 'file' && retroFile) fd.append('retro_file', retroFile);
    else if (retroPath) fd.append('retro_path', retroPath);
    fd.append('rarity', rarity);
    fd.append('notes', notes);
    if (authorMode === 'user' && authorUserId) {
      fd.append('author_user_id', String(authorUserId));
    } else if (authorMode === 'text' && authorText.trim()) {
      fd.append('author_name', authorText.trim());
    }
    selectedWrestlerIds.forEach((id) => fd.append('wrestler_ids[]', String(id)));
    selectedPpvSpecifics.forEach((s) => fd.append('ppv_ids[]', String(s.id)));
    selectedBeltIds.forEach((id) => fd.append('belt_ids[]', String(id)));
    tagNames.split(',').map((s) => s.trim()).filter(Boolean).forEach((n) => fd.append('tag_names[]', n));
    if (price.trim() !== '') fd.append('price', price.trim());
    else fd.append('price', '');
    fd.append('shop_disabled', shopDisabled ? '1' : '0');
    fd.append('reference_url', referenceUrl.trim());
    fd.append('reference_string', referenceString.trim());
    fd.append('reference_data', referenceData.trim());
    try {
      const mergeReferenceData = (c: Card): Card => ({
        ...c,
        reference_data: referenceData.trim() || null,
      });
      if (isEdit) {
        const result = (await apiUpdateCard(card.id, fd)) as any;
        if (result?.errors) {
          setErrors(result.errors);
        } else {
          onUpdated?.(mergeReferenceData(result?.data ?? result));
        }
      } else {
        const result = (await apiCreateCard(fd)) as any;
        if (result?.errors) {
          setErrors(result.errors);
        } else {
          onCreated(mergeReferenceData(result?.data ?? result));
        }
      }
    } catch {
      setErrors({ general: 'Si è verificato un errore. Riprova.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Dialog>
        <ModalHeader>
          <ModalTitle>{isEdit ? `Modifica Carta #${card.id}` : 'Nuova Carta'}</ModalTitle>
          <CloseBtn type="button" onClick={onClose}>✕</CloseBtn>
        </ModalHeader>

        <ModalBody>
          {refDataLoading ? (
            <SpinnerText>Caricamento dati...</SpinnerText>
          ) : (
            <>
              {errors.general && <GeneralError>{errors.general}</GeneralError>}

              {/* Fronte */}
              <FormGroup>
                <Label>Fronte</Label>
                <ToggleRow>
                  <ToggleBtn type="button" active={fronteMode === 'file'} onClick={() => setFronteMode('file')}>File</ToggleBtn>
                  <ToggleBtn type="button" active={fronteMode === 'path'} onClick={() => setFronteMode('path')}>Path</ToggleBtn>
                </ToggleRow>
                {fronteMode === 'file' ? (
                  <>
                    <Input
                      ref={fronteInputRef}
                      type="file"
                      accept="image/*"
                      disabled={isSaving}
                      onChange={(e) => setFronteFile(e.target.files?.[0] ?? null)}
                    />
                    {fronteFile && <ErrorMsg style={{ color: 'rgba(255,255,255,0.5)' }}>{fronteFile.name}</ErrorMsg>}
                  </>
                ) : (
                  <Input
                    type="text"
                    placeholder="es: img/cards/fronte.jpg"
                    value={frontePath}
                    disabled={isSaving}
                    onChange={(e) => setFrontePath(e.target.value)}
                  />
                )}
                {errors.fronte_file && <ErrorMsg>{errors.fronte_file}</ErrorMsg>}
                {errors.fronte_path && <ErrorMsg>{errors.fronte_path}</ErrorMsg>}
              </FormGroup>

              {/* Retro */}
              <FormGroup>
                <Label>Retro</Label>
                <ToggleRow>
                  <ToggleBtn type="button" active={retroMode === 'file'} onClick={() => setRetroMode('file')}>File</ToggleBtn>
                  <ToggleBtn type="button" active={retroMode === 'path'} onClick={() => setRetroMode('path')}>Path</ToggleBtn>
                </ToggleRow>
                {retroMode === 'file' ? (
                  <>
                    <Input
                      ref={retroInputRef}
                      type="file"
                      accept="image/*"
                      disabled={isSaving}
                      onChange={(e) => setRetroFile(e.target.files?.[0] ?? null)}
                    />
                    {retroFile && <ErrorMsg style={{ color: 'rgba(255,255,255,0.5)' }}>{retroFile.name}</ErrorMsg>}
                  </>
                ) : (
                  <Input
                    type="text"
                    placeholder="es: img/cards/retro.jpg"
                    value={retroPath}
                    disabled={isSaving}
                    onChange={(e) => setRetroPath(e.target.value)}
                  />
                )}
                {errors.retro_file && <ErrorMsg>{errors.retro_file}</ErrorMsg>}
                {errors.retro_path && <ErrorMsg>{errors.retro_path}</ErrorMsg>}
              </FormGroup>

              {/* Rarità */}
              <FormGroup>
                <Label>Rarità</Label>
                <Select value={rarity} disabled={isSaving} onChange={(e) => setRarity(e.target.value as CardRarity)}>
                  {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
                </Select>
                {errors.rarity && <ErrorMsg>{errors.rarity}</ErrorMsg>}
              </FormGroup>

              {/* Wrestler */}
              <FormGroup>
                <Label>Wrestler</Label>
                <SearchableMultiSelect
                  items={wrestlerItems}
                  selected={selectedWrestlerIds}
                  onChange={setSelectedWrestlerIds}
                  placeholder="Cerca wrestler..."
                  disabled={isSaving}
                />
                {errors.wrestler_ids && <ErrorMsg>{errors.wrestler_ids}</ErrorMsg>}
              </FormGroup>

              {/* PPV — selezione a due livelli */}
              <FormGroup>
                <Label>PPV</Label>
                <MultiSelectWrapper>
                  <MultiSelectInput
                    type="text"
                    value={generalQuery}
                    placeholder="Cerca PPV (es: WrestleMania)..."
                    disabled={isSaving}
                    onChange={(e) => { setGeneralQuery(e.target.value); setGeneralOpen(true); }}
                    onFocus={() => setGeneralOpen(true)}
                    onBlur={() => setTimeout(() => setGeneralOpen(false), 150)}
                  />
                  {generalOpen && filteredGenerals.length > 0 && (
                    <Dropdown>
                      {filteredGenerals.map((g) => (
                        <DropdownItem
                          key={g.id}
                          isSelected={selectedGeneralId === g.id}
                          onMouseDown={() => {
                            setSelectedGeneralId(g.id);
                            setGeneralQuery(g.name);
                            setGeneralOpen(false);
                          }}
                        >
                          {g.name}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  )}
                </MultiSelectWrapper>
                {selectedGeneral && (
                  <SpecificSelect
                    value=""
                    disabled={isSaving}
                    onChange={(e) => {
                      const specificId = Number(e.target.value);
                      if (!specificId) return;
                      const specific = (selectedGeneral.ppvs ?? []).find((s: any) => s.id === specificId);
                      if (!specific || selectedPpvSpecifics.some((s) => s.id === specificId)) return;
                      const year = (specific as any).year ?? (specific as any).date?.split('-')[2] ?? '';
                      setSelectedPpvSpecifics((prev) => [
                        ...prev,
                        { id: specificId, label: `${selectedGeneral.name} ${year}`.trim() },
                      ]);
                    }}
                  >
                    <option value="">-- Seleziona edizione --</option>
                    {(selectedGeneral.ppvs ?? [])
                      .filter((s: any) => !selectedPpvSpecifics.some((sel) => sel.id === s.id))
                      .map((s: any) => {
                        const year = s.year ?? s.date?.split('-')[2] ?? s.id;
                        return (
                          <option key={s.id} value={s.id}>
                            {selectedGeneral.name} {year}
                          </option>
                        );
                      })}
                  </SpecificSelect>
                )}
                {selectedPpvSpecifics.length > 0 && (
                  <ChipsRow>
                    {selectedPpvSpecifics.map((s) => (
                      <Chip key={s.id}>
                        {s.label}
                        <ChipRemove
                          type="button"
                          disabled={isSaving}
                          onClick={() => setSelectedPpvSpecifics((prev) => prev.filter((p) => p.id !== s.id))}
                        >
                          ✕
                        </ChipRemove>
                      </Chip>
                    ))}
                  </ChipsRow>
                )}
                {errors.ppv_ids && <ErrorMsg>{errors.ppv_ids}</ErrorMsg>}
              </FormGroup>

              {/* Belt */}
              <FormGroup>
                <Label>Cinture</Label>
                <SearchableMultiSelect
                  items={beltItems}
                  selected={selectedBeltIds}
                  onChange={setSelectedBeltIds}
                  placeholder="Cerca cintura..."
                  disabled={isSaving}
                />
                {errors.belt_ids && <ErrorMsg>{errors.belt_ids}</ErrorMsg>}
              </FormGroup>

              {/* Tag */}
              <FormGroup>
                <Label>Tag (separati da virgola)</Label>
                <Input
                  type="text"
                  placeholder="es: finisher, main-event, ppv2024"
                  value={tagNames}
                  disabled={isSaving}
                  onChange={(e) => setTagNames(e.target.value)}
                />
              </FormGroup>

              {/* Autore */}
              <FormGroup>
                <Label>Autore</Label>
                {isEdit && (card?.author_user || card?.author_name) && (
                  <CurrentAuthorBadge>
                    Attuale:&nbsp;
                    <span>
                      {card.author_user
                        ? `${card.author_user.username}${card.author_user.nickname_forum ? ` — "${card.author_user.nickname_forum}"` : ''}`
                        : card.author_name}
                    </span>
                    &nbsp;({card.author_user ? 'utente registrato' : 'testo libero'})
                  </CurrentAuthorBadge>
                )}
                <ToggleRow>
                  <ToggleBtn type="button" active={authorMode === 'user'} onClick={() => setAuthorMode('user')}>Utente registrato</ToggleBtn>
                  <ToggleBtn type="button" active={authorMode === 'text'} onClick={() => setAuthorMode('text')}>Testo libero</ToggleBtn>
                </ToggleRow>
                {authorMode === 'user' ? (
                  <MultiSelectWrapper>
                    <MultiSelectInput
                      type="text"
                      value={authorUserQuery}
                      placeholder="Cerca utente per username..."
                      disabled={isSaving}
                      onChange={(e) => { setAuthorUserQuery(e.target.value); setAuthorUserId(null); setAuthorUserOpen(true); }}
                      onFocus={() => setAuthorUserOpen(true)}
                      onBlur={() => setTimeout(() => setAuthorUserOpen(false), 150)}
                    />
                    {authorUserOpen && filteredUsers.length > 0 && (
                      <Dropdown>
                        {filteredUsers.map((u) => (
                          <DropdownItem
                            key={u.id}
                            isSelected={authorUserId === u.id}
                            onMouseDown={() => {
                              setAuthorUserId(u.id);
                              setAuthorUserQuery(u.username);
                              setAuthorUserOpen(false);
                            }}
                          >
                            {u.username}{u.nickname_forum ? ` — "${u.nickname_forum}"` : ''}
                          </DropdownItem>
                        ))}
                      </Dropdown>
                    )}
                    {selectedUser && (
                      <ChipsRow>
                        <Chip>
                          {selectedUser.username}
                          {selectedUser.nickname_forum && ` — "${selectedUser.nickname_forum}"`}
                          <ChipRemove
                            type="button"
                            disabled={isSaving}
                            onClick={() => { setAuthorUserId(null); setAuthorUserQuery(''); }}
                          >
                            ✕
                          </ChipRemove>
                        </Chip>
                      </ChipsRow>
                    )}
                  </MultiSelectWrapper>
                ) : (
                  <Input
                    type="text"
                    placeholder="Nome autore (testo libero)"
                    value={authorText}
                    disabled={isSaving}
                    onChange={(e) => setAuthorText(e.target.value)}
                  />
                )}
                {errors.author_user_id && <ErrorMsg>{errors.author_user_id}</ErrorMsg>}
                {errors.author_name && <ErrorMsg>{errors.author_name}</ErrorMsg>}
              </FormGroup>

              {/* Prezzo negozio */}
              <FormGroup>
                <Label>Prezzo negozio (crediti)</Label>
                <Input
                  type="number"
                  min={1}
                  placeholder="Lascia vuoto = non in vendita"
                  value={price}
                  disabled={isSaving}
                  onChange={(e) => setPrice(e.target.value)}
                />
                {defaultPrices[rarity] != null && (
                  <PriceHint
                    type="button"
                    onClick={() => setPrice(String(defaultPrices[rarity]))}
                  >
                    Usa default {rarity}: {defaultPrices[rarity]} cr.
                  </PriceHint>
                )}
              </FormGroup>

              <FormGroup>
                <ToggleRow>
                  <input
                    type="checkbox"
                    id="shop_disabled"
                    checked={shopDisabled}
                    disabled={isSaving}
                    onChange={e => setShopDisabled(e.target.checked)}
                  />
                  <Label htmlFor="shop_disabled" style={{ cursor: 'pointer' }}>
                    Disabilita dal negozio (mantiene il prezzo)
                  </Label>
                </ToggleRow>
              </FormGroup>

              {/* Riferimento */}
              <FormGroup>
                <Label>URL riferimento</Label>
                <Input
                  type="text"
                  placeholder="https://... (opzionale, max 2048 caratteri)"
                  value={referenceUrl}
                  disabled={isSaving}
                  onChange={(e) => setReferenceUrl(e.target.value)}
                />
                {errors.reference_url && <ErrorMsg>{errors.reference_url}</ErrorMsg>}
              </FormGroup>

              <FormGroup>
                <Label>Testo riferimento</Label>
                <Input
                  type="text"
                  placeholder="Testo da mostrare per il riferimento (opzionale, max 500 caratteri)"
                  value={referenceString}
                  disabled={isSaving}
                  onChange={(e) => setReferenceString(e.target.value)}
                />
                {errors.reference_string && <ErrorMsg>{errors.reference_string}</ErrorMsg>}
              </FormGroup>

              <FormGroup>
                <Label>Data riferimento</Label>
                <Input
                  type="date"
                  value={referenceData}
                  disabled={isSaving}
                  onChange={(e) => setReferenceData(e.target.value)}
                />
                {errors.reference_data && <ErrorMsg>{errors.reference_data}</ErrorMsg>}
              </FormGroup>

              {/* Note */}
              <FormGroup>
                <Label>Note</Label>
                <Textarea
                  placeholder="Note opzionali..."
                  value={notes}
                  disabled={isSaving}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <BtnSecondary type="button" disabled={isSaving} onClick={onClose}>Annulla</BtnSecondary>
          <BtnPrimary type="button" disabled={isSaving || refDataLoading} onClick={handleSubmit}>
            {isSaving ? 'Salvataggio...' : isEdit ? 'Salva Modifiche' : 'Salva'}
          </BtnPrimary>
        </ModalFooter>
      </Dialog>
    </Overlay>
  );
};

export default CardCreateModal;
